import React, { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Files, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const DOC_TYPE_KEYWORDS = [
  { v: "id", re: /(id[_\s-]?proof|aadhar|aadhaar|\bpan\b|passport|voter)/i },
  { v: "certificate", re: /(certificate|certification|degree|marksheet|diploma)/i },
  { v: "address_proof", re: /(address|utility[_\s-]?bill|rent[_\s-]?agreement)/i },
  { v: "contract", re: /(contract|offer[_\s-]?letter|employment[_\s-]?agreement)/i },
  { v: "nda", re: /\bnda\b/i },
  { v: "payslip", re: /(payslip|salary[_\s-]?slip)/i },
];

function guessDocType(filename) {
  for (const { v, re } of DOC_TYPE_KEYWORDS) if (re.test(filename)) return v;
  return "other";
}

function guessUser(filename, users) {
  const norm = filename.toLowerCase().replace(/[_.\-]+/g, " ");
  const tokens = norm.split(/\s+/).filter(Boolean);
  let best = null, bestScore = 0;
  for (const u of users) {
    const parts = (u.name || "").toLowerCase().split(/\s+/).filter((p) => p.length > 2);
    let score = 0;
    for (const p of parts) if (tokens.includes(p)) score++; // whole-word match only — avoids "Dev" matching "Devcontract"
    if (score > bestScore) { bestScore = score; best = u; }
  }
  return bestScore > 0 ? best : null;
}

/** Feb 2026 — Bulk Document Upload (Founder/Admin only). Auto-matches each
 * file's team member + doc type from its filename, lets the admin review /
 * reassign before confirming, then routes each file into that person's
 * "My Documents" folder via the existing POST /hr-docs endpoint. */
export default function BulkDocumentUploadDialog({ users, docTypes, onDone }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const internalUsers = useMemo(() => users.filter((u) => u.role !== "client"), [users]);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = files.map((file) => {
      const matchedUser = guessUser(file.name, internalUsers);
      return {
        _uid: `${file.name}-${file.size}-${Math.random()}`,
        file,
        title: file.name.replace(/\.[^.]+$/, ""),
        user_id: matchedUser?.id || "",
        doc_type: guessDocType(file.name),
        expiry_date: "",
        matched: !!matchedUser,
      };
    });
    setItems((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const updateItem = (uid, patch) => setItems((prev) => prev.map((it) => it._uid === uid ? { ...it, ...patch } : it));
  const removeItem = (uid) => setItems((prev) => prev.filter((it) => it._uid !== uid));

  const confirmUpload = async () => {
    const missing = items.filter((it) => !it.user_id);
    if (missing.length) { toast.error(`${missing.length} file(s) still need a team member selected`); return; }
    setUploading(true);
    let ok = 0, fail = 0, firstError = "";
    for (const it of items) {
      try {
        const fd = new FormData();
        fd.append("doc_type", it.doc_type);
        fd.append("title", it.title);
        fd.append("user_id", it.user_id);
        if (it.expiry_date) fd.append("expiry_date", it.expiry_date);
        fd.append("file", it.file);
        await api.post("/hr-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) { fail++; if (!firstError) firstError = formatApiError(e); }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} document${ok !== 1 ? "s" : ""} filed`);
    if (fail) toast.error(`${fail} file${fail !== 1 ? "s" : ""} failed to upload${firstError ? `: ${firstError}` : ""}`);
    setItems([]);
    setOpen(false);
    onDone?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setItems([]); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm" data-testid="bulk-upload-docs-btn">
          <Files size={14} className="mr-1.5" /> Bulk upload documents
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-3xl sm:max-w-3xl" data-testid="bulk-upload-docs-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk upload documents</DialogTitle>
          <DialogDescription>
            Pick multiple files — we'll guess the team member &amp; document type from each filename (e.g. "Shubham_IDProof.pdf"). Review and fix any misses below before confirming.
          </DialogDescription>
        </DialogHeader>

        <Input type="file" multiple onChange={onFiles} className="rounded-sm mt-2" data-testid="bulk-upload-file-input" />

        {items.length > 0 && (
          <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
            {items.map((it) => (
              <div key={it._uid} className="border border-slate-200 rounded-sm p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end" data-testid={`bulk-upload-row-${it._uid}`}>
                <div className="sm:col-span-2 lg:col-span-3 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate" title={it.file.name}>{it.file.name}</div>
                  <div className="text-[10px] mt-0.5 inline-flex items-center gap-1 text-slate-400">
                    {it.matched ? <CheckCircle2 size={11} className="text-green-600" /> : <AlertTriangle size={11} className="text-orange-500" />}
                    {it.matched ? "Auto-matched" : "No match — pick manually"}
                  </div>
                </div>
                <div className="lg:col-span-3">
                  <Label className="label-overline text-[9px] block truncate">Team member</Label>
                  <Select value={it.user_id || ""} onValueChange={(v) => updateItem(it._uid, { user_id: v, matched: true })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid={`bulk-upload-user-${it._uid}`}><SelectValue placeholder="Pick a person" /></SelectTrigger>
                    <SelectContent>
                      {internalUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="lg:col-span-2">
                  <Label className="label-overline text-[9px] block truncate">Doc type</Label>
                  <Select value={it.doc_type} onValueChange={(v) => updateItem(it._uid, { doc_type: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid={`bulk-upload-doctype-${it._uid}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{docTypes.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="lg:col-span-3">
                  <Label className="label-overline text-[9px] block truncate">Expiry (optional)</Label>
                  <Input type="date" className="rounded-sm mt-1" value={it.expiry_date} onChange={(e) => updateItem(it._uid, { expiry_date: e.target.value })} data-testid={`bulk-upload-expiry-${it._uid}`} />
                </div>
                <div className="lg:col-span-1 flex justify-end">
                  <button type="button" onClick={() => removeItem(it._uid)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`bulk-upload-remove-${it._uid}`}>
                    <XCircle size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={confirmUpload} disabled={uploading || items.length === 0}
            className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="bulk-upload-confirm-btn"
          >
            {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
            {uploading ? "Uploading…" : `Confirm & upload ${items.length || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
